import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, Flame, CheckCircle2, CalendarDays, Lock, CreditCard, LogOut, MessageSquareText, Gift, Copy, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, todayStr, urgencyColor } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ParentPage() {
  const { user, logout, refresh } = useAuth();
  const [token, setToken] = useState(sessionStorage.getItem("parent_token") || "");
  const [smsForm, setSmsForm] = useState({
    phone: user?.child?.phone || "",
    sms_enabled: user?.child?.sms_enabled !== false,
    parent_phone: user?.parent_phone || "",
  });
  const [smsSaving, setSmsSaving] = useState(false);

  const saveSmsSettings = async () => {
    setSmsSaving(true);
    try {
      await api.patch("/settings/sms", smsForm);
      await refresh();
      toast.success("SMS settings saved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save settings.");
    }
    setSmsSaving(false);
  };
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    if (token) api.get("/referral").then((r) => setReferral(r.data)).catch(() => {});
  }, [token]);

  const referralLink = referral ? `${window.location.origin}/auth?ref=${referral.code}` : "";

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Invite link copied!");
    } catch {
      toast.error("Couldn't copy — long-press the link to copy it.");
    }
  };

  const sendTestSms = async () => {
    setSmsSending(true);
    try {
      await api.post("/sms/test");
      toast.success("Test SMS sent via your GHL number!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the SMS.");
    }
    setSmsSending(false);
  };

  const authHeaders = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

  const loadReport = (t) => {
    setLoading(true);
    api.get("/parent/report", { params: { today: todayStr() }, ...authHeaders(t) })
      .then((r) => setReport(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          sessionStorage.removeItem("parent_token");
          setToken("");
          toast.error("Parent session expired — enter the password again.");
        } else {
          toast.error("Couldn't load the report.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) loadReport(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async () => {
    if (!password) return;
    setVerifying(true);
    try {
      const r = await api.post("/parent/verify", { password });
      sessionStorage.setItem("parent_token", r.data.token);
      setToken(r.data.token);
      setPassword("");
      loadReport(r.data.token);
    } catch {
      toast.error("Wrong password.");
    }
    setVerifying(false);
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await api.post(`/parent/send-report?today=${todayStr()}`, null, authHeaders(token));
      toast.success(`Report emailed to ${r.data.sent_to}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the email.");
    }
    setSending(false);
  };

  if (!token) {
    return (
      <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-5">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Parent view</h1>
          <p className="text-gray-600 text-sm mt-1">This area is for parents.</p>
        </div>
        <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 space-y-3" data-testid="parent-lock-card">
          <div className="flex items-center gap-2 font-bold"><Lock size={18} /> Your account password</div>
          <input
            type="password"
            className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
            placeholder="Enter your account password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify()}
            data-testid="parent-password-input"
          />
          <button
            onClick={verify}
            disabled={verifying}
            data-testid="parent-unlock-button"
            className="w-full bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 disabled:opacity-60"
          >
            {verifying ? "Checking..." : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Parent view</h1>
        <p className="text-gray-600 text-sm mt-1">Weekly progress, straight from Mia. A report is auto-emailed every Sunday 5pm (Sydney time).</p>
      </div>

      {loading ? (
        <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-8 flex flex-col items-center gap-3" data-testid="parent-report-loading">
          <Loader2 size={32} className="animate-spin" />
          <p className="text-sm text-gray-600">Mia's writing this week's summary...</p>
        </div>
      ) : report && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#FFE066] border-2 border-black rounded-2xl shadow-[4px_4px_0px_rgba(0,0,0,1)] p-4" data-testid="parent-checkins-stat">
              <Flame size={22} className="text-[#FF6B6B]" />
              <p className="font-heading text-2xl font-semibold mt-1">{report.stats.checkins_this_week}/7</p>
              <p className="text-xs font-bold text-gray-700">check-ins this week</p>
            </div>
            <div className="bg-[#51CF66] border-2 border-black rounded-2xl shadow-[4px_4px_0px_rgba(0,0,0,1)] p-4" data-testid="parent-chunks-stat">
              <CheckCircle2 size={22} />
              <p className="font-heading text-2xl font-semibold mt-1">{report.stats.chunks_completed_this_week.length}</p>
              <p className="text-xs font-bold text-gray-700">work chunks done</p>
            </div>
          </div>

          <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6" data-testid="parent-summary-card">
            <h2 className="font-heading text-lg font-semibold mb-2">Mia's summary</h2>
            <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">{report.summary}</p>
          </div>

          <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 space-y-3" data-testid="parent-deadlines-card">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><CalendarDays size={18} /> Upcoming deadlines</h2>
            {report.stats.upcoming_deadlines.length === 0 && <p className="text-sm text-gray-600">Nothing scheduled.</p>}
            {report.stats.upcoming_deadlines.map((u, i) => (
              <div key={`${u.title}-${u.subject}-${u.due_date}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                <div>
                  <p className="font-bold">{u.title}</p>
                  <p className="text-xs text-gray-600">{u.subject} · {u.type}</p>
                </div>
                <span className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full border-2 border-black text-xs font-bold ${urgencyColor(u.days_left)}`}>
                  {u.days_left}d
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={sendEmail}
            disabled={sending}
            data-testid="send-report-button"
            className="w-full bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
            Email this report now
          </button>

          <button
            onClick={sendTestSms}
            disabled={smsSending}
            data-testid="send-test-sms-button"
            className="w-full bg-white border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {smsSending ? <Loader2 size={18} className="animate-spin" /> : <MessageSquareText size={18} />}
            Send a test SMS nudge now
          </button>

          <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5 space-y-3" data-testid="sms-settings-card">
            <div className="flex items-center gap-2 font-bold"><Smartphone size={18} /> SMS nudge settings</div>
            <p className="text-sm text-gray-700">Mia texts a daily urgency digest (7:30am) when deadlines get close.</p>
            <input
              type="tel"
              className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
              placeholder="Teen's mobile (04XX XXX XXX)"
              value={smsForm.phone}
              onChange={(e) => setSmsForm({ ...smsForm, phone: e.target.value })}
              data-testid="sms-phone-input"
            />
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-bold">Daily SMS nudges</span>
              <button
                onClick={() => setSmsForm({ ...smsForm, sms_enabled: !smsForm.sms_enabled })}
                data-testid="sms-enabled-toggle"
                className={`w-14 h-8 rounded-full border-2 border-black relative transition-colors ${smsForm.sms_enabled ? "bg-[#51CF66]" : "bg-gray-200"}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white border-2 border-black rounded-full transition-all ${smsForm.sms_enabled ? "left-[26px]" : "left-0.5"}`} />
              </button>
            </label>
            <input
              type="tel"
              className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
              placeholder="Your mobile (optional — Friday wins text)"
              value={smsForm.parent_phone}
              onChange={(e) => setSmsForm({ ...smsForm, parent_phone: e.target.value })}
              data-testid="parent-phone-input"
            />
            <p className="text-xs text-gray-500 -mt-1">Add your number and Mia texts you a Friday arvo summary of the week's wins.</p>
            <button
              onClick={saveSmsSettings}
              disabled={smsSaving}
              data-testid="sms-settings-save-button"
              className="w-full bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {smsSaving && <Loader2 size={16} className="animate-spin" />} Save SMS settings
            </button>
          </div>

          {referral && (
            <div className="bg-[#F2C6DE] border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5 space-y-3" data-testid="referral-card">
              <div className="flex items-center gap-2 font-bold"><Gift size={18} /> Invite a friend, get a free month</div>
              <p className="text-sm text-gray-700">When a family you invite subscribes, you get one month of StudySpark free — automatically.</p>
              <div className="flex gap-2">
                <input readOnly value={referralLink} data-testid="referral-link-input"
                  className="flex-1 min-w-0 bg-white border-2 border-black rounded-xl px-3 py-2.5 text-xs font-mono" />
                <button onClick={copyReferral} data-testid="referral-copy-button"
                  className="shrink-0 px-4 bg-white border-2 border-black rounded-xl font-bold shadow-[2px_2px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all">
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-xs font-bold" data-testid="referral-stats">
                {referral.referred_count} friend{referral.referred_count === 1 ? "" : "s"} joined · {referral.rewarded_count} free month{referral.rewarded_count === 1 ? "" : "s"} earned
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Link to="/billing" data-testid="parent-billing-link"
              className="flex items-center gap-1.5 text-sm font-bold underline">
              <CreditCard size={15} /> Manage subscription
            </Link>
            <button onClick={logout} data-testid="parent-logout-button"
              className="flex items-center gap-1.5 text-sm font-bold text-gray-500 hover:text-black transition-colors">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
